/**
 * Shared helpers and constants for the Patterns page and pattern detail.
 */

export const EMPTY_SENTINEL = Infinity;

export const TABS = {
  PERFORMANCE: "performance",
  VIDEOS: "videos",
};

export const SORT_KEYS = {
  PATTERN: "pattern",
  COUNT: "count",
  LIKES: "likes",
  LIKES_VS: "likes_vs",
  COMMENTS: "comments",
  COMMENTS_VS: "comments_vs",
  VIEWS: "views",
  VIEWS_VS: "views_vs",
};

export const BASELINE_COMPARISON_TOOLTIP =
  "How this pattern performs vs the median of all audience videos.";

export const GENERATING_IDEAS_MESSAGES = [
  "Generating genius ideas...",
  "Generating scroll-stopping ideas...",
  "Generating viral-worthy concepts...",
  "Generating audience magnets...",
  "Generating thumb-stopping ideas...",
  "Generating conversion-ready concepts...",
  "Generating engagement gold...",
  "Generating brand-perfect ideas...",
  "Generating pattern-powered concepts...",
  "Generating creator-approved ideas...",
];

export const formatNumber = (num) => {
  if (num == null) return "—";
  const n = Math.round(Number(num));
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
};

export const pctDiffFromRatio = (ratio) => {
  if (ratio == null) return null;
  const isAbove = ratio >= 1;
  const pct = ratio >= 1 ? ((ratio - 1) * 100).toFixed(0) : ((1 - ratio) * 100).toFixed(0);
  return { text: isAbove ? `+${pct}%` : `-${pct}%`, above: isAbove };
};

export const getPatternDisplayName = (pattern) =>
  pattern?.name ?? pattern?.id ?? "—";

export const isViewsEmpty = (v) =>
  v == null ||
  v === "" ||
  (typeof Number(v) === "number" && (Number.isNaN(Number(v)) || Number(v) === 0));

/**
 * Return the value to use for sorting a pattern by sortKey (or EMPTY_SENTINEL / -Infinity for missing).
 */
export function getSortValue(pattern, sortKey) {
  switch (sortKey) {
    case SORT_KEYS.PATTERN:
      return (pattern.name ?? pattern.id ?? "").toLowerCase();
    case SORT_KEYS.COUNT: {
      const v = pattern.stats?.video_count;
      return v == null ? EMPTY_SENTINEL : v;
    }
    case SORT_KEYS.VIEWS: {
      const v = pattern.stats?.views;
      return isViewsEmpty(v) ? EMPTY_SENTINEL : Number(v);
    }
    case SORT_KEYS.LIKES: {
      const v = pattern.stats?.likes;
      return v == null ? EMPTY_SENTINEL : v;
    }
    case SORT_KEYS.COMMENTS: {
      const v = pattern.stats?.comments;
      return v == null ? EMPTY_SENTINEL : v;
    }
    case SORT_KEYS.VIEWS_VS:
      return pattern.baseline_comparison?.views ?? -Infinity;
    case SORT_KEYS.LIKES_VS:
      return pattern.baseline_comparison?.likes ?? -Infinity;
    case SORT_KEYS.COMMENTS_VS:
      return pattern.baseline_comparison?.comments ?? -Infinity;
    default:
      return null;
  }
}

/**
 * Get x-position (0-100) for a value within a distribution.
 */
export function getValuePositionInDistribution(value, distribution) {
  if (value == null || !distribution?.length) return null;
  const n = distribution.length;
  if (value <= distribution[0].start) return 0;
  const last = distribution[n - 1];
  if (value >= last.end) return 100;
  for (let i = 0; i < n; i++) {
    const b = distribution[i];
    if (value < b.start) continue;
    if (i < n - 1 && value > b.end) continue;
    const span = b.end - b.start || 1;
    const fraction = Math.min(1, (value - b.start) / span);
    return ((i + fraction) / n) * 100;
  }
  return 100;
}

/**
 * Find which bucket index a value falls into.
 */
export function getBucketIndexForValue(value, distribution) {
  if (value == null || !distribution?.length) return null;
  const n = distribution.length;
  for (let i = 0; i < n; i++) {
    const b = distribution[i];
    if (value < b.start) return i > 0 ? i - 1 : 0;
    if (value <= b.end) return i;
  }
  return n - 1;
}

/**
 * Table column config: { label, tooltip, sortKey, align }.
 */
export const TABLE_COLUMNS = [
  { label: "Pattern", tooltip: "Pattern name or identifier", sortKey: SORT_KEYS.PATTERN },
  { label: "Count", tooltip: "Number of videos in this pattern", sortKey: SORT_KEYS.COUNT },
  {
    label: "Likes",
    tooltip: "Median likes per video in this pattern.",
    sortKey: SORT_KEYS.LIKES,
    align: "right",
  },
  {
    label: "vs avg",
    tooltip: "Likes vs baseline: how this pattern performs vs the median of all audience videos.",
    sortKey: SORT_KEYS.LIKES_VS,
  },
  {
    label: "Comments",
    tooltip: "Median comments per video in this pattern.",
    sortKey: SORT_KEYS.COMMENTS,
    align: "right",
  },
  {
    label: "vs avg",
    tooltip: "Comments vs baseline: how this pattern performs vs the median of all audience videos.",
    sortKey: SORT_KEYS.COMMENTS_VS,
  },
  {
    label: "Views",
    tooltip: "Median views per video in this pattern. View data is not always available (e.g. for some Instagram videos).",
    sortKey: SORT_KEYS.VIEWS,
    align: "right",
  },
  {
    label: "vs avg",
    tooltip: "Views vs baseline: how this pattern performs vs the median of all audience videos.",
    sortKey: SORT_KEYS.VIEWS_VS,
  },
];
